import React, { useState } from "react";
import { Button, Modal, Steps, Input, Form, Result, Space, Select } from "antd";
import { useStepsForm } from "sunflower-antd";
import type { SelectProps } from "antd";
import AddressInput from "../../FormInputs/AddressInput";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  group: any;
  showModal: Function;
  handleUpdate: Function;
}

let options: SelectProps["options"] = [];

const EditGroupModal: React.FC<IModalProps> = ({
  show,
  group,
  showModal,
  handleUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    defaultFormValues: group,
    async submit(values: any) {
      setLoading(true);

      const response = await handleUpdate(group.id, {
        GroupType: values.GroupType,
        GroupName: values.GroupName,
        TelNumber: values.TelNumber,
        Cellphone: values.Cellphone,
        FaxNumber: values.FaxNumber,
        Email: values.Email,
        FSPNumber: values.FSPNumber,
        AddressLine1: values.AddressLine1,
        AddressLine2: values.AddressLine2,
        Suburb: values.Suburb,
        CityTown: values.CityTown,
        Province: values.Province,
        PostalCode: values.PostalCode,
      });

      if (response.success) {
        setLoading(false);
        return true;
      }
      setErrorMsg(response.message);
      setLoading(false);
      return false;
    },
    total: 4,
  });

  // Step Styling
  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 17 },
    labelAlign: "right" as const,
  };

  const tailLayout = {
    labelCol: { span: 6 },
    wrapperCol: { offset: 6, span: 17 },
  };

  const formList = [
    <>
      <Form.Item
        name="GroupType"
        label="Group Type"
        initialValue={group?.GroupType || "Internal"}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          size="large"
          placeholder="Select Group Type"
          options={[
            { value: "Internal", label: "Internal" },
            { value: "External", label: "External" },
          ]}
        />
      </Form.Item>
      <Form.Item
        name="GroupName"
        label="Group Name"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Group Name" />
      </Form.Item>
      <Form.Item
        name="TelNumber"
        label="Telephone Number"
        rules={[
          {
            required: true,
            pattern: new RegExp(/^(?:\+27|0)[1-9]\d{8}$/),
            message: "Please enter your Telephone Number",
          },
        ]}
      >
        <Input size="large" placeholder="Tel Number" />
      </Form.Item>
      <Form.Item
        name="Cellphone"
        label="Cellphone"
        rules={[
          {
            required: true,
            pattern: new RegExp(/^(?:\+27|0)[1-9]\d{8}$/),
            message: "Please enter your Cellphone Number",
          },
        ]}
      >
        <Input size="large" placeholder="Cellphone" />
      </Form.Item>
      <Form.Item name="FaxNumber" label="Fax Number">
        <Input size="large" placeholder="Fax Number" />
      </Form.Item>
      <Form.Item
        name="Email"
        label="Email"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Email" />
      </Form.Item>
      <Form.Item name="FSPNumber" label="FSP Number">
        <Input size="large" placeholder="FSP Number" />
      </Form.Item>

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Space>
      </Form.Item>
    </>,

    <>
      <AddressInput form={form} />

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            style={{ marginBottom: 10 }}
            block
            size="large"
            loading={formLoading}
            onClick={() => {
              submit().then((result) => {
                if (result) {
                  gotoStep(current + 1);
                } else if (!result) {
                  gotoStep(current + 2);
                }
              });
            }}
          >
            Submit
          </Button>
        </Space>
      </Form.Item>
    </>,
  ];

  return (
    <Modal
      title="Edit Group"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={900}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 2 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Basic Information" />
              <Step title="Address" />
            </Steps>
          </div>
        )}

        <div style={{ flex: 1, marginTop: 24 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 1100 }}>
            {formList[current]}
          </Form>

          {current === 2 && (
            <Result status="success" title="Successfully updated">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleModal}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 3 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditGroupModal;
